import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EllipsisOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  SlidersOutlined
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Popover,
  Segmented,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import {
  BUDGET_RETURN_PARAM,
  MARKETING_TILE_ANCHOR
} from "../../../constants/marketing-source-performance.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { leadSourceIcon } from "../../../containers/Leads/constants/lead-source-icons.tsx"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { TextInput } from "../../Input/TextInput.tsx"
import type {
  EngagementDrill,
  MarketingData,
  MarketingPeriodType,
  MarketingSourceRow
} from "../types"
import { fetchMarketingSourcePerformance } from "./marketingSourcePerformance.api.ts"

const PERIODS: MarketingPeriodType[] = [
  "week",
  "month",
  "quarter",
  "last_12_months"
]

const SHARE_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#8B5CF6",
  "#22C55E",
  "#F59E0B",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
  "#F97316",
  "#6366F1"
]
const TOTAL_COLOR = "#1E3A5F"

const { Text } = Typography

interface MarketingSourceTableProps {
  initial: MarketingData
  onDrill?: (drill: EngagementDrill) => void
}

// A `YYYY-MM-DD` is a calendar date; parsed as UTC it reads a day early west of Greenwich.
const localDate = (value: string) => new Date(`${value}T00:00:00`)

/** The anchor one period before or after the one shown. Last 12 Months steps a month. */
export const stepAnchor = (
  period: MarketingData["period"],
  direction: 1 | -1
): string => {
  if (period.type === "last_12_months")
    return dayjs(period.end)
      .startOf("month")
      .add(direction, "month")
      .format("YYYY-MM-DD")
  return direction === 1
    ? dayjs(period.end).add(1, "day").format("YYYY-MM-DD")
    : dayjs(period.start).subtract(1, "day").format("YYYY-MM-DD")
}

/** New leads against the period before: a whole percentage, "new" when there was
 *  nothing to compare with, and nothing at all when both are zero. */
export const leadTrend = (
  current: number,
  previous: number
): number | "new" | null => {
  if (previous === 0) return current > 0 ? "new" : null
  return Math.round(((current - previous) / previous) * 100)
}

const money = (value: string) =>
  budgetFormat(value, { minimumFractionDigits: 2 })

const shareOf = (budget: string, total: string) =>
  Number(total) > 0 ? Math.round((Number(budget) / Number(total)) * 100) : 0

const ShareBar = ({ percent, color }: { percent: number; color: string }) => (
  <Flex align={"center"} gap={8} style={{ minWidth: 150 }}>
    <span style={{ minWidth: 42, textAlign: "right", whiteSpace: "nowrap" }}>
      {percent}%
    </span>
    <div
      role={"presentation"}
      style={{
        flex: 1,
        height: 8,
        borderRadius: 4,
        background: "#EEF2F7",
        overflow: "hidden"
      }}
    >
      <div
        style={{
          width: `${percent}%`,
          height: "100%",
          borderRadius: 4,
          background: color
        }}
      />
    </div>
  </Flex>
)

/** Marketing Source Performance: each lead source's new and engaged leads over
 *  one week, month, quarter or the last 12 months, against its allocated
 *  budget. It picks its own period — Home's date filter does not apply,
 *  because a budget is only defined per calendar month. */
const MarketingSourceTableImpl = ({
  initial,
  onDrill
}: MarketingSourceTableProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const tileRef = useRef<HTMLDivElement>(null)
  // The tile lives on Home and on the Portfolio dashboard: the budget page
  // sends the reader back to whichever one they came from.
  const configureBudgets = () =>
    navigate(
      `${RoutesPaths.settings.leadSourceBudget}?${new URLSearchParams({
        [BUDGET_RETURN_PARAM]: location.pathname
      }).toString()}`
    )

  // Back from the budget page: bring the tile into view.
  useEffect(() => {
    if (location.hash === `#${MARKETING_TILE_ANCHOR}`) {
      tileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [location.hash])
  const [data, setData] = useState<MarketingData>(initial)
  const [period, setPeriod] = useState<MarketingPeriodType>(initial.period.type)
  const [anchor, setAnchor] = useState<string>(initial.period.start)
  const [sources, setSources] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [pickerSearch, setPickerSearch] = useState("")
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const latestRequest = useRef(0)
  // The choices behind what is on screen: a failed load falls back to them, so
  // the period picker and ‹ › never disagree with the table.
  const shown = useRef({
    period: initial.period.type,
    anchor: initial.period.start,
    sources: [] as string[]
  })

  const load = async (next: {
    period: MarketingPeriodType
    anchor: string
    sources: string[]
  }) => {
    const request = ++latestRequest.current
    setLoading(true)
    setFailed(false)
    const response = await fetchMarketingSourcePerformance(next).catch(
      () => null
    )
    if (request !== latestRequest.current) return
    setLoading(false)
    if (response?.ok && response.data?.rows) {
      setData(response.data)
      shown.current = next
    } else {
      setFailed(true)
      setPeriod(shown.current.period)
      setAnchor(shown.current.anchor)
      setSources(shown.current.sources)
    }
  }

  // A refresh of the card hands over a new first page. It already is what the
  // tile shows by default, so only a period or sources of the reader's own
  // choosing needs reading again.
  useEffect(() => {
    const onDefault =
      period === initial.period.type &&
      (anchor === "" || anchor === initial.period.start) &&
      sources.length === 0
    if (onDefault) {
      latestRequest.current += 1
      setData(initial)
      setFailed(false)
      setLoading(false)
      shown.current = {
        period: initial.period.type,
        anchor: initial.period.start,
        sources: []
      }
      return
    }
    void load({ period, anchor, sources })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- re-syncs when the host hands in a new initial payload; the picked period, anchor and sources are read as they are at that moment
  }, [initial])

  const choose = (
    next: Partial<{
      period: MarketingPeriodType
      anchor: string
      sources: string[]
    }>
  ) => {
    const merged = { period, anchor, sources, ...next }
    setPeriod(merged.period)
    setAnchor(merged.anchor)
    setSources(merged.sources)
    void load(merged)
  }

  const locale = i18n.language
  const monthLabel = (value: string) =>
    localDate(value).toLocaleDateString(locale, {
      month: "short",
      year: "numeric"
    })
  const periodLabel = (() => {
    const { type, start, end } = data.period
    if (type === "week")
      return t("marketingSourcePerformance.weekOf", {
        date: localDate(start).toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          year: "numeric"
        })
      })
    if (type === "quarter")
      return t("marketingSourcePerformance.quarterLabel", {
        quarter: Math.floor(localDate(start).getMonth() / 3) + 1,
        year: localDate(start).getFullYear()
      })
    if (type === "last_12_months")
      return t("marketingSourcePerformance.lastTwelveLabel", {
        start: monthLabel(start),
        end: monthLabel(end)
      })
    return monthLabel(start)
  })()

  const sourceName = (row: {
    is_no_source?: boolean
    source: string
    label: string
  }) =>
    row.is_no_source || row.source === "none"
      ? t("marketingSourcePerformance.noLeadSource")
      : row.label

  const drill = (kind: EngagementDrill["kind"], row: MarketingSourceRow) =>
    onDrill?.({
      kind,
      start: data.period.start,
      end: data.period.end,
      label: `${sourceName(row)} · ${periodLabel}`,
      filters: { source: row.source },
      companyWide: true
    })

  const count = (
    kind: EngagementDrill["kind"],
    value: number,
    row: MarketingSourceRow
  ) =>
    value > 0 && onDrill ? (
      <Button
        type={"link"}
        size={"small"}
        style={{ padding: 0, height: "auto", fontWeight: "inherit" }}
        onClick={() => drill(kind, row)}
      >
        {value}
      </Button>
    ) : (
      value
    )

  const cost = (value: string | null, emptyHint: string) =>
    value === null ? (
      <Tooltip title={emptyHint}>
        <Text type={"secondary"}>—</Text>
      </Tooltip>
    ) : (
      money(value)
    )

  const trendPill = (current: number, previous: number) => {
    const trend = leadTrend(current, previous)
    if (trend === null) return <Text type={"secondary"}>—</Text>
    if (trend === 0)
      return (
        <Tooltip
          title={t("marketingSourcePerformance.trendHint", { previous })}
        >
          <Tag bordered={false} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
            0%
          </Tag>
        </Tooltip>
      )
    const up = trend === "new" || trend > 0
    return (
      <Tooltip title={t("marketingSourcePerformance.trendHint", { previous })}>
        <Tag
          bordered={false}
          color={up ? "success" : "error"}
          icon={up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          style={{ marginInlineEnd: 0, fontWeight: 600 }}
        >
          {trend === "new"
            ? t("marketingSourcePerformance.trendNew")
            : `${Math.abs(trend)}%`}
        </Tag>
      </Tooltip>
    )
  }

  const budgetTitle =
    data.period.type === "week" ? (
      <Tooltip title={t("marketingSourcePerformance.weeklyHint")}>
        <span>{t("marketingSourcePerformance.columns.budget")}</span>
      </Tooltip>
    ) : (
      t("marketingSourcePerformance.columns.budget")
    )

  const byNumber =
    (key: keyof MarketingSourceRow) =>
    (a: MarketingSourceRow, b: MarketingSourceRow) =>
      Number(a[key] ?? -1) - Number(b[key] ?? -1)

  const maxSources = data.max_sources
  // The comparison as it stands: the explicit picks, else the top five on screen.
  const compared = sources.length ? sources : data.rows.map((row) => row.source)
  const optionName = (option: MarketingData["available_sources"][number]) =>
    option.source === "none"
      ? t("marketingSourcePerformance.noLeadSource")
      : option.is_active
        ? option.label
        : `${option.label} (${t("marketingSourcePerformance.inactive")})`
  const toggleSource = (value: string, checked: boolean) =>
    choose({
      sources: checked
        ? [...compared, value].slice(0, maxSources)
        : compared.filter((picked) => picked !== value)
    })

  // Out of the whole spend, so the displayed total can be under 100%.
  const shareBase = data.totals.all_sources_budget ?? data.totals.budget
  const colorOf = new Map(
    data.rows.map((row, index) => [
      row.source,
      SHARE_COLORS[index % SHARE_COLORS.length]
    ])
  )
  const matches = (name: string, text: string) =>
    name
      .toLocaleLowerCase(locale)
      .includes(text.trim().toLocaleLowerCase(locale))
  const shownRows = search.trim()
    ? data.rows.filter((row) => matches(sourceName(row), search))
    : data.rows

  const picker = (
    <Flex vertical gap={8} style={{ width: 280 }}>
      <TextInput
        disableAutoCapitalize
        allowClear
        size={"small"}
        prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
        placeholder={t("marketingSourcePerformance.searchSources")}
        aria-label={t("marketingSourcePerformance.sources")}
        value={pickerSearch}
        onChange={(event) => setPickerSearch(event.target.value)}
      />
      <Flex vertical gap={6} style={{ maxHeight: 260, overflowY: "auto" }}>
        {data.available_sources
          .filter((option) => matches(optionName(option), pickerSearch))
          .map((option) => {
            const checked = compared.includes(option.source)
            return (
              <Checkbox
                key={option.source}
                checked={checked}
                disabled={
                  (checked && compared.length <= 1) ||
                  (!checked && compared.length >= maxSources)
                }
                onChange={(event) =>
                  toggleSource(option.source, event.target.checked)
                }
              >
                {optionName(option)}
              </Checkbox>
            )
          })}
      </Flex>
      <Flex justify={"space-between"} align={"center"}>
        <Text type={"secondary"} style={{ fontSize: 12 }}>
          {t("marketingSourcePerformance.maxSources", { max: maxSources })}
        </Text>
        <Button
          size={"small"}
          type={"link"}
          disabled={!sources.length}
          onClick={() => choose({ sources: [] })}
        >
          {t("marketingSourcePerformance.resetTopFive")}
        </Button>
      </Flex>
    </Flex>
  )

  return (
    <Flex vertical gap={12} id={MARKETING_TILE_ANCHOR} ref={tileRef}>
      <Flex gap={8} wrap align={"center"} justify={"space-between"}>
        <Flex gap={8} wrap align={"center"}>
          <Segmented<MarketingPeriodType>
            size={"small"}
            value={period}
            onChange={(value) => choose({ period: value, anchor: "" })}
            options={PERIODS.map((value) => ({
              value,
              label: t(`marketingSourcePerformance.periods.${value}`)
            }))}
          />
          <Flex align={"center"} gap={4}>
            <Button
              size={"small"}
              type={"text"}
              icon={<LeftOutlined />}
              aria-label={t("marketingSourcePerformance.previous")}
              onClick={() => choose({ anchor: stepAnchor(data.period, -1) })}
            />
            <Text strong>{periodLabel}</Text>
            {data.period.in_progress && (
              <Tag color={"blue"} style={{ marginInlineEnd: 0 }}>
                {t("marketingSourcePerformance.inProgress")}
              </Tag>
            )}
            <Button
              size={"small"}
              type={"text"}
              icon={<RightOutlined />}
              aria-label={t("marketingSourcePerformance.next")}
              disabled={!data.period.has_next}
              onClick={() => choose({ anchor: stepAnchor(data.period, 1) })}
            />
          </Flex>
        </Flex>
        <Flex gap={8} wrap align={"center"}>
          <TextInput
            disableAutoCapitalize
            allowClear
            prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
            placeholder={t("marketingSourcePerformance.searchSources")}
            aria-label={t("marketingSourcePerformance.searchSources")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ width: 220 }}
          />
          <Button icon={<SettingOutlined />} onClick={configureBudgets}>
            {t("marketingSourcePerformance.configureBudget")}
          </Button>
          <Popover trigger={"click"} placement={"bottomRight"} content={picker}>
            <Button icon={<SlidersOutlined />} style={{ fontWeight: 600 }}>
              {t("marketingSourcePerformance.selectSources", {
                count: compared.length,
                max: maxSources
              })}
            </Button>
          </Popover>
        </Flex>
      </Flex>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} title={false} />
      ) : failed ? (
        <Flex vertical align={"center"} gap={8} style={{ padding: "16px 0" }}>
          <Text type={"secondary"}>
            {t("marketingSourcePerformance.loadFailed")}
          </Text>
          <Button
            size={"small"}
            onClick={() => void load({ period, anchor, sources })}
          >
            {t("marketingSourcePerformance.retry")}
          </Button>
        </Flex>
      ) : data.rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("marketingSourcePerformance.empty")}
        />
      ) : (
        <Table<MarketingSourceRow>
          size={"middle"}
          rowKey={"source"}
          pagination={false}
          scroll={{ x: "max-content" }}
          dataSource={shownRows}
          locale={{ emptyText: t("marketingSourcePerformance.noSearchMatch") }}
          columns={[
            {
              key: "compare",
              width: 48,
              render: (_, row) => (
                <Checkbox
                  checked
                  disabled={compared.length <= 1}
                  aria-label={t("marketingSourcePerformance.removeSource", {
                    source: sourceName(row)
                  })}
                  onChange={() => toggleSource(row.source, false)}
                />
              )
            },
            {
              title: t("marketingSourcePerformance.columns.source"),
              key: "source",
              sorter: (a, b) =>
                sourceName(a).localeCompare(sourceName(b), locale),
              render: (_, row) => (
                <Flex gap={10} align={"center"}>
                  <span
                    aria-hidden
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      background: "#F1F5F9"
                    }}
                  >
                    {leadSourceIcon(row.source)}
                  </span>
                  <span>{sourceName(row)}</span>
                  {!row.is_active && !row.is_no_source && (
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {t("marketingSourcePerformance.inactive")}
                    </Tag>
                  )}
                </Flex>
              )
            },
            {
              title: budgetTitle,
              dataIndex: "budget",
              align: "center",
              sorter: byNumber("budget"),
              render: (value: string, row) =>
                !row.budget_set && !row.is_no_source ? (
                  <Tooltip title={t("marketingSourcePerformance.notSetHint")}>
                    <Text type={"secondary"}>{money(value)}</Text>
                  </Tooltip>
                ) : (
                  money(value)
                )
            },
            {
              title: t("marketingSourcePerformance.columns.newLeads"),
              dataIndex: "new_leads",
              align: "center",
              defaultSortOrder: "descend",
              sorter: byNumber("new_leads"),
              render: (value: number, row) => count("added", value, row)
            },
            {
              title: t("marketingSourcePerformance.columns.engagedLeads"),
              dataIndex: "engaged_leads",
              align: "center",
              sorter: byNumber("engaged_leads"),
              render: (value: number, row) => count("engaged", value, row)
            },
            {
              title: t("marketingSourcePerformance.columns.costPerNew"),
              dataIndex: "cost_per_new_lead",
              align: "center",
              sorter: byNumber("cost_per_new_lead"),
              render: (value: string | null) =>
                cost(value, t("marketingSourcePerformance.noNewLeads"))
            },
            {
              title: t("marketingSourcePerformance.columns.costPerEngaged"),
              dataIndex: "cost_per_engaged_lead",
              align: "center",
              sorter: byNumber("cost_per_engaged_lead"),
              render: (value: string | null) =>
                cost(value, t("marketingSourcePerformance.noEngagedLeads"))
            },
            {
              title: (
                <Tooltip title={t("marketingSourcePerformance.shareHint")}>
                  <span>{t("marketingSourcePerformance.columns.share")}</span>
                </Tooltip>
              ),
              key: "share",
              render: (_, row) => (
                <ShareBar
                  percent={shareOf(row.budget, shareBase)}
                  color={colorOf.get(row.source) ?? SHARE_COLORS[0]}
                />
              )
            },
            {
              title: t("marketingSourcePerformance.columns.trend"),
              key: "trend",
              align: "center",
              render: (_, row) =>
                trendPill(row.new_leads, row.previous_new_leads)
            },
            {
              title: t("marketingSourcePerformance.columns.actions"),
              key: "actions",
              align: "center",
              width: 80,
              render: (_, row) => (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      {
                        key: "added",
                        label: t("marketingSourcePerformance.actions.viewNew"),
                        disabled: !row.new_leads || !onDrill,
                        onClick: () => drill("added", row)
                      },
                      {
                        key: "engaged",
                        label: t(
                          "marketingSourcePerformance.actions.viewEngaged"
                        ),
                        disabled: !row.engaged_leads || !onDrill,
                        onClick: () => drill("engaged", row)
                      },
                      {
                        key: "budget",
                        label: t(
                          "marketingSourcePerformance.actions.editBudget"
                        ),
                        disabled: row.is_no_source,
                        onClick: configureBudgets
                      }
                    ]
                  }}
                >
                  <Button
                    type={"text"}
                    size={"small"}
                    icon={<EllipsisOutlined />}
                    aria-label={t("marketingSourcePerformance.actions.label", {
                      source: sourceName(row)
                    })}
                  />
                </Dropdown>
              )
            }
          ]}
          summary={() => (
            <Table.Summary.Row
              style={{ fontWeight: 700, background: "#F8FAFC" }}
            >
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1}>
                <Tooltip title={t("marketingSourcePerformance.totalHint")}>
                  <span>
                    {t("marketingSourcePerformance.totalSources", {
                      count: data.rows.length
                    })}
                  </span>
                </Tooltip>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align={"center"}>
                {money(data.totals.budget)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align={"center"}>
                {data.totals.new_leads}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align={"center"}>
                {data.totals.engaged_leads}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align={"center"}>
                {cost(
                  data.totals.cost_per_new_lead,
                  t("marketingSourcePerformance.noNewLeads")
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align={"center"}>
                {cost(
                  data.totals.cost_per_engaged_lead,
                  t("marketingSourcePerformance.noEngagedLeads")
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <ShareBar
                  percent={shareOf(data.totals.budget, shareBase)}
                  color={TOTAL_COLOR}
                />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align={"center"}>
                {trendPill(
                  data.totals.new_leads,
                  data.totals.previous_new_leads
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} />
            </Table.Summary.Row>
          )}
        />
      )}
    </Flex>
  )
}

export const MarketingSourceTable = React.memo(MarketingSourceTableImpl)
